/**
 * Serialization utilities for universal export state.
 *
 * React elements (icon property) cannot survive JSON.stringify/parse.
 * These functions strip non-serializable properties before storage
 * and rehydrate them from config on read.
 */

import type {
  ExportFilterSection,
  ExportModalState,
  ExportOption,
} from '@/components/export/universal/types';

/**
 * Strip non-serializable properties (icon) from export state before storage.
 * Icons contain React elements which have Symbol and Function references
 * that JSON.stringify silently drops.
 */
export function stripNonSerializableProps(
  state: ExportModalState
): ExportModalState {
  const strippedFilters: Record<string, readonly ExportOption[]> = {};

  for (const [sectionId, options] of Object.entries(state.filters)) {
    strippedFilters[sectionId] = options.map(
      ({ icon: _icon, ...rest }) => rest
    );
  }

  return { ...state, filters: strippedFilters };
}

/**
 * Rehydrate non-serializable properties from config source of truth.
 * Looks up each stored option's value in the config's options and
 * defaultSelected arrays to restore the icon property.
 */
export function rehydrateState(
  storedState: ExportModalState,
  filterSections: readonly Readonly<ExportFilterSection>[]
): ExportModalState {
  // Build lookup: sectionId -> (value -> canonical ExportOption with icon)
  const configLookup = new Map<string, Map<string | number, ExportOption>>();

  for (const section of filterSections) {
    const valueMap = new Map<string | number, ExportOption>();

    for (const option of section.options) {
      valueMap.set(option.value, option as ExportOption);
    }

    // Also include defaultSelected options (may contain values not in options)
    if (section.defaultSelected) {
      for (const option of section.defaultSelected) {
        if (!valueMap.has(option.value)) {
          valueMap.set(option.value, option as ExportOption);
        }
      }
    }

    configLookup.set(section.id, valueMap);
  }

  // Rehydrate each stored filter option with icon from config
  const rehydratedFilters: Record<string, readonly ExportOption[]> = {};

  for (const [sectionId, options] of Object.entries(storedState.filters)) {
    const sectionLookup = configLookup.get(sectionId);

    if (!sectionLookup) {
      // Unknown section, pass through as-is
      rehydratedFilters[sectionId] = options;
      continue;
    }

    rehydratedFilters[sectionId] = options.map(storedOption => {
      const configOption = sectionLookup.get(storedOption.value);
      if (configOption && configOption.icon) {
        return { ...storedOption, icon: configOption.icon };
      }
      return storedOption;
    });
  }

  return { ...storedState, filters: rehydratedFilters };
}
